// Página MyOrders - Mis Pedidos
// Permite a los usuarios logueados ver sus pedidos

import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { getOrdersByEmail } from '../services/orders.service'
import { formatCurrency, formatDate } from '../utils/helpers'
import Button from '../components/Button'
import './MyOrders.css'

/**
 * Página MyOrders
 * Muestra los pedidos del usuario logueado
 */
function MyOrders() {
  const navigate = useNavigate()
  const { currentUser, loading: authLoading } = useAuth()
  
  const [orders, setOrders] = useState([])
  const [filteredOrders, setFilteredOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Filtros
  const [statusFilter, setStatusFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')

  // Redirigir si no está logueado
  useEffect(() => {
    if (!authLoading && !currentUser) {
      navigate('/login')
    }
  }, [currentUser, authLoading, navigate])

  const fetchOrders = useCallback(async () => {
    if (!currentUser?.email) return
    try {
      setLoading(true)
      setError(null)
      const ordersData = await getOrdersByEmail(currentUser.email)
      setOrders(ordersData)
    } catch (err) {
      console.error('Error cargando pedidos:', err)
      setError('Error al cargar tus pedidos')
    } finally {
      setLoading(false)
    }
  }, [currentUser])

  const applyFilters = useCallback(() => {
    let filtered = [...orders]

    // Filtrar por estado
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter)
    }

    // Filtrar por búsqueda (número de pedido)
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(order => {
        const orderNumber = (order.orderNumber || '').toLowerCase()
        return orderNumber.includes(search)
      })
    }

    // Ordenar por fecha (más recientes primero)
    filtered.sort((a, b) => {
      let aDate = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0
      let bDate = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0
      return bDate - aDate
    })

    setFilteredOrders(filtered)
  }, [orders, statusFilter, searchTerm])

  // Cargar pedidos cuando el usuario esté disponible
  useEffect(() => {
    if (currentUser && currentUser.email) {
      fetchOrders()
    }
  }, [currentUser, fetchOrders])

  // Aplicar filtros
  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendiente',
      confirmed: 'Confirmado',
      processing: 'En Proceso',
      shipped: 'Enviado',
      delivered: 'Entregado',
      cancelled: 'Cancelado'
    }
    return labels[status] || status
  }

  const getStatusClass = (status) => {
    return `status-badge status-${status}`
  }

  if (authLoading || loading) {
    return (
      <div className="my-orders-page">
        <div className="loading-state">
          <p>Cargando tus pedidos...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="my-orders-page">
        <div className="error-state">
          <p>{error}</p>
          <Button onClick={fetchOrders}>Reintentar</Button>
        </div>
      </div>
    )
  }

  if (!currentUser) {
    return null // Se redirige automáticamente
  }

  return (
    <div className="my-orders-page">
      {/* Header */}
      <div className="my-orders-header">
        <div>
          <h1>Mis Pedidos</h1>
          <p className="my-orders-subtitle">
            Aquí puedes ver el estado de todos tus pedidos
          </p>
        </div>
        <Button onClick={() => navigate('/productos')} variant="outline">
          Seguir Comprando
        </Button>
      </div>

      {/* Estadísticas rápidas */}
      {orders.length > 0 && (
        <div className="my-orders-stats">
          <div className="stat-card">
            <div className="stat-value">{orders.length}</div>
            <div className="stat-label">Total de Pedidos</div>
          </div>
          <div className="stat-card stat-pending">
            <div className="stat-value">
              {orders.filter(o => o.status === 'pending').length}
            </div>
            <div className="stat-label">Pendientes</div>
          </div>
          <div className="stat-card stat-processing">
            <div className="stat-value">
              {orders.filter(o => o.status === 'processing' || o.status === 'confirmed').length}
            </div>
            <div className="stat-label">En Proceso</div>
          </div>
          <div className="stat-card stat-delivered">
            <div className="stat-value">
              {orders.filter(o => o.status === 'delivered').length}
            </div>
            <div className="stat-label">Entregados</div>
          </div>
        </div>
      )}

      {/* Filtros */}
      {orders.length > 0 && (
        <div className="my-orders-filters">
          <div className="filter-group">
            <label htmlFor="statusFilter">Estado:</label>
            <select
              id="statusFilter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Todos</option>
              <option value="pending">Pendiente</option>
              <option value="confirmed">Confirmado</option>
              <option value="processing">En Proceso</option>
              <option value="shipped">Enviado</option>
              <option value="delivered">Entregado</option>
              <option value="cancelled">Cancelado</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="searchTerm">Buscar:</label>
            <input
              type="text"
              id="searchTerm"
              placeholder="Número de pedido..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Lista de pedidos */}
      <div className="my-orders-list">
        {orders.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h2>No tienes pedidos aún</h2>
            <p>Cuando realices tu primer pedido, aparecerá aquí</p>
            <Button onClick={() => navigate('/productos')} variant="primary">
              Ver Productos
            </Button>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <h2>No se encontraron pedidos</h2>
            <p>Intenta ajustar los filtros de búsqueda</p>
            <Button 
              onClick={() => {
                setStatusFilter('all')
                setSearchTerm('')
              }} 
              variant="outline"
            >
              Limpiar Filtros
            </Button>
          </div>
        ) : (
          filteredOrders.map((order) => {
            const date = order.createdAt?.toDate 
              ? formatDate(order.createdAt.toDate())
              : formatDate(new Date())

            return (
              <div key={order.id} className="order-card">
                <div className="order-card-header">
                  <div className="order-card-info">
                    <Link 
                      to={`/mis-pedidos/${order.id}`}
                      className="order-number-link"
                    >
                      <h3>Pedido {order.orderNumber || order.id}</h3>
                    </Link>
                    <p className="order-date">Fecha: {date}</p>
                  </div>
                  <div className="order-card-status">
                    <span className={getStatusClass(order.status)}>
                      {getStatusLabel(order.status)}
                    </span>
                  </div>
                </div>

                <div className="order-card-body">
                  <div className="order-items-preview">
                    {order.items?.slice(0, 3).map((item, index) => (
                      <div key={index} className="order-item-preview">
                        <span className="item-name">{item.productName}</span>
                        {item.variant && (
                          <span className="item-variant">
                            ({item.variant.type}: {item.variant.name})
                          </span>
                        )}
                        <span className="item-quantity">x{item.quantity}</span>
                      </div>
                    ))}
                    {order.items?.length > 3 && (
                      <p className="more-items">
                        y {order.items.length - 3} producto(s) más...
                      </p>
                    )}
                  </div>

                  <div className="order-card-total">
                    <span className="total-label">Total:</span>
                    <span className="total-value">{formatCurrency(order.total || 0)}</span>
                  </div>
                  <div className="order-card-total">
                    <span className="total-label">Medio de pago:</span>
                    <span className="total-value">{order.payment?.label || 'Pendiente'}</span>
                  </div>
                </div>

                <div className="order-card-footer">
                  <Link to={`/mis-pedidos/${order.id}`}>
                    <Button variant="outline" size="small">
                      Ver Detalle
                    </Button>
                  </Link>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export default MyOrders

